import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

// Import HRDashboard component inline since it lives in Dashboard.js
// We recreate it here as a standalone page
export default function HRDashboardPage() {
  const { user } = useAuth();

  if (!['super_admin', 'hr_officer'].includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const token = localStorage.getItem('bb_token');
    fetch('/api/dashboard/hr', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(r => r.json()).then(d => {
      if (!d.error) setData(d);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fmt = v => v ?? 0;
  const fmtDate = v => v ? new Date(v).toLocaleDateString('en-NG', {day:'numeric',month:'short'}) : '—';

  const Tile = ({label, value, icon, color, sub}) => (
    <div style={{background:'#fff',borderRadius:12,padding:'18px 20px',
      boxShadow:'0 2px 8px rgba(0,0,0,.06)',borderLeft:'4px solid '+color}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontSize:11,color:'#8A8A8A',textTransform:'uppercase',
            letterSpacing:'.07em',marginBottom:6}}>{label}</div>
          <div style={{fontSize:28,fontWeight:700,color:'#2C1F16'}}>{value}</div>
          {sub && <div style={{fontSize:11,color:'#8A8A8A',marginTop:4}}>{sub}</div>}
        </div>
        <div style={{fontSize:28}}>{icon}</div>
      </div>
    </div>
  );

  if (loading) return <div className="spinner" style={{marginTop:40}}/>;
  if (!data) return <div className="empty-state"><h3>Unable to load HR dashboard</h3></div>;

  const { stats, upcoming_birthdays, recent_leave, recent_prospects } = data;

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',color:'var(--forest)'}}>
            👥 HR Dashboard
          </h2>
          <p className="text-muted text-sm">Bloans Creations Ltd. — People & Culture Overview</p>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
        <Tile label="Total Staff" value={fmt(stats.total_staff)} icon="👥" color="#556B2F" />
        <Tile label="Pending Leave" value={fmt(stats.pending_leave)} icon="🏖️" color="#A8893D"
          sub={stats.pending_leave > 0 ? 'Needs approval' : 'All clear'} />
        <Tile label="Active Onboarding" value={fmt(stats.pending_onboarding)} icon="🎯" color="#3B2A21" />
        <Tile label="In Offboarding" value={fmt(stats.pending_offboarding)} icon="🚪" color="#8F2E24" />
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
        <Tile label="Attendance (7 days)" value={fmt(stats.attendance_today)} icon="⏰" color="#556B2F" />
        <Tile label="Bio-Data Missing" value={fmt(stats.biodata_missing)} icon="📋" color="#A8893D"
          sub="Staff yet to submit" />
        <Tile label="Not on Payroll" value={fmt(stats.payroll_not_enrolled)} icon="💳" color="#8F2E24"
          sub="Enrolment pending" />
        <Tile label="Active Staff" value={fmt(stats.active_staff)} icon="✅" color="#2E6B3A" />
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16}}>
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:14}}>🏖️ Pending Leave Requests</div>
          {recent_leave.length === 0 ? (
            <p style={{fontSize:13,color:'#8A8A8A'}}>No pending requests</p>
          ) : recent_leave.map((lr,i) => (
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{fontWeight:600,color:'#2C1F16'}}>{lr.staff_name}</div>
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                {lr.leave_type} · {fmtDate(lr.start_date)} – {fmtDate(lr.end_date)}
              </div>
            </div>
          ))}
          {stats.pending_leave > 5 && (
            <a href="/leave" style={{fontSize:12,color:'#556B2F',marginTop:8,display:'block'}}>View all →</a>
          )}
        </div>

        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:14}}>🎂 Upcoming Birthdays</div>
          {upcoming_birthdays.length === 0 ? (
            <p style={{fontSize:13,color:'#8A8A8A'}}>No birthdays in the next 14 days</p>
          ) : upcoming_birthdays.map((b,i) => (
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{fontWeight:600,color:'#2C1F16'}}>{b.name}</div>
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                {new Date(b.date_of_birth).toLocaleDateString('en-NG',{day:'numeric',month:'long'})}
              </div>
            </div>
          ))}
        </div>

        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:14}}>🎯 Recent Prospects</div>
          {recent_prospects.length === 0 ? (
            <p style={{fontSize:13,color:'#8A8A8A'}}>No prospects</p>
          ) : recent_prospects.map((p,i) => (
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{fontWeight:600,color:'#2C1F16'}}>{p.full_name}</div>
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                {p.position} · <span style={{
                  color: p.status==='offer_sent'?'#A8893D':p.status==='hired'?'#556B2F':'#8A8A8A'
                }}>{p.status?.replace(/_/g,' ')}</span>
              </div>
            </div>
          ))}
          <a href="/prospects" style={{fontSize:12,color:'#556B2F',marginTop:8,display:'block'}}>View all →</a>
        </div>
      </div>
    </div>
  );
}
